import { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useViewMode } from '@/stores/viewMode'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useModules, ModuleType } from '@/hooks/useModules'
import { clinicModuleService } from '@/services/clinic_module_service'

export default function ModulesPage() {
  const { id: clinicId } = useViewMode()
  const { token } = useAuth()
  const { modules, isLoading, isError, mutate } = useModules(clinicId)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Create a state to track module toggle states
  const [moduleStates, setModuleStates] = useState<Record<string, boolean>>({})

  // Initialize module states from fetched data
  useEffect(() => {
    if (modules.length > 0 && Object.keys(moduleStates).length === 0) {
      const initialStates: Record<string, boolean> = {}
      modules.forEach((module) => {
        initialStates[module.module_type] = module.is_active
      })
      setModuleStates(initialStates)
    }
  }, [modules, moduleStates])

  // Handle toggle change with mutual exclusion logic
  const handleToggleChange = (moduleType: string, checked: boolean) => {
    setModuleStates((prev) => {
      const newStates = { ...prev }

      if (checked) {
        if (moduleType === ModuleType.THIRD_PARTY_QUEUE) {
          // If third party queue is selected, disable other two modules
          newStates[ModuleType.QUEUE_SYSTEM] = false
          newStates[ModuleType.APPOINTMENT_SYSTEM] = false
          newStates[ModuleType.THIRD_PARTY_QUEUE] = true
        } else if (
          moduleType === ModuleType.QUEUE_SYSTEM ||
          moduleType === ModuleType.APPOINTMENT_SYSTEM
        ) {
          // If queue system or appointment system is selected, disable third party queue
          newStates[ModuleType.THIRD_PARTY_QUEUE] = false
          newStates[moduleType] = true
        } else {
          newStates[moduleType] = checked
        }
      } else {
        // If unchecking, just set to false
        newStates[moduleType] = false
      }

      return newStates
    })
  }

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!clinicId || !token) return

    setIsSubmitting(true)
    try {
      const result = await clinicModuleService.updateClinicModules(clinicId, moduleStates, token)

      if (result.success) {
        toast.success('更新成功')
        mutate()
      } else {
        throw new Error(result.error?.message || '更新失敗')
      }
    } catch {
      toast.error('更新失敗')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isError) {
    return (
      <div>
        <div className="text-center text-destructive">載入模組列表失敗。請稍後再試。</div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-[200px] mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">模組管理</h2>
        <p className="text-muted-foreground mt-2">管理診所可用的功能模組</p>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {modules.map((module) => (
              <div
                key={module.module_type}
                className="flex items-center justify-between py-3 border-b"
              >
                <div>
                  <h3 className="text-lg font-medium">
                    {clinicModuleService.getModuleName(module.module_type)}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {module.module_type === ModuleType.QUEUE_SYSTEM
                      ? '啟用診所叫號系統功能'
                      : module.module_type === ModuleType.APPOINTMENT_SYSTEM
                        ? '啟用診所預約系統功能'
                        : '啟用第三方叫號系統功能'}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id={`module-${module.module_type}`}
                    checked={moduleStates[module.module_type] || false}
                    onCheckedChange={(checked) => handleToggleChange(module.module_type, checked)}
                  />
                  <Label htmlFor={`module-${module.module_type}`}>
                    {moduleStates[module.module_type] ? '啟用' : '停用'}
                  </Label>
                </div>
              </div>
            ))}

            <div className="flex justify-end mt-6">
              <Button onClick={handleSaveChanges} disabled={isSubmitting}>
                {isSubmitting ? '儲存中...' : '儲存變更'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
